import { NextRequest } from "next/server";
import { z } from "zod";
import { apiError, apiOk, requireApiToken } from "@/lib/api-auth";
import { prisma } from "@/lib/db";
import { slugify } from "@/lib/slug-vi";

export const runtime = "nodejs";

// GET /api/v1/tags?q=
export async function GET(req: NextRequest) {
  const ctx = await requireApiToken(req, ["post:read"]);
  if (ctx instanceof Response) return ctx;
  const url = new URL(req.url);
  const q = url.searchParams.get("q")?.trim();
  const tags = await prisma.tag.findMany({
    where: q
      ? { name: { contains: q, mode: "insensitive" } }
      : undefined,
    orderBy: { name: "asc" },
    take: 100,
    select: {
      id: true,
      slug: true,
      name: true,
      _count: { select: { articles: true } },
    },
  });
  return apiOk({
    items: tags.map((t) => ({
      id: t.id,
      slug: t.slug,
      name: t.name,
      articleCount: t._count.articles,
    })),
  });
}

// POST /api/v1/tags — upsert single tag
const Body = z.object({
  name: z.string().min(1).max(60),
  slug: z.string().max(60).optional(),
});

export async function POST(req: NextRequest) {
  const ctx = await requireApiToken(req, ["post:write"]);
  if (ctx instanceof Response) return ctx;
  let body: unknown;
  try {
    body = await req.json();
  } catch {
    return apiError(400, "bad_json", "Body không phải JSON.");
  }
  const parsed = Body.safeParse(body);
  if (!parsed.success)
    return apiError(400, "bad_body", "Body không hợp lệ.", parsed.error.flatten());
  const slug = slugify(parsed.data.slug ?? parsed.data.name);
  if (!slug) return apiError(400, "bad_slug", "Slug rỗng.");
  const tag = await prisma.tag.upsert({
    where: { slug },
    update: { name: parsed.data.name },
    create: { slug, name: parsed.data.name },
  });
  return apiOk(tag, 201);
}
